import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { crudStatusEnum } from '../../../../constants';
import { useCreateDumpMutation, useUpdateDumpMutation } from '../../../../hooks/mutations/useDumpMutation';
import { Dump } from '../../../../services/apiSLTP/materials/dumps';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import CustomTextInput from '../../../ui/CustomTextInput';
import ToggleLine from '../../../ui/ToggleLine';
import ModalBody from '../../ModalBody';

interface ModalProps {
    dump: Dump | null;
    isEditMode?: boolean;
    showEditModal: boolean;
    setShowEditModal: React.Dispatch<React.SetStateAction<boolean>>;
    refetch: () => void;
}

const UpdateDumpModal: React.FC<ModalProps> = ({ dump, isEditMode = true, showEditModal, setShowEditModal, refetch }) => {
    const { t } = useTranslation();
    const [selectedDesignation, setSelectedDesignation] = useState(dump?.designation ?? '');
    const [selectedCodeProC, setSelectedCodeProC] = useState(dump?.codeProC ?? '');
    const [selectedVolume, setSelectedVolume] = useState(dump?.volume ?? null);
    const [selectedDisplayOrder, setSelectedDisplayOrder] = useState(dump?.displayOrder ?? null);
    const [selectedCrudStatus, setSelectedCrudStatus] = useState(dump?.crudStatus ?? crudStatusEnum.OK);

    const { mutate: mutateDump } = useUpdateDumpMutation({
        onSuccess: () => refetch(),
        onError: (error) => console.error('Error updating dump:', error.message),
    });

    const { mutate: addDump } = useCreateDumpMutation({
        onSuccess: () => refetch(),
        onError: (error) => console.error('Error updating dump:', error.message),
    });

    const handleActiveChange = (isActive: boolean) => {
        setSelectedCrudStatus(isActive ? crudStatusEnum.OK : crudStatusEnum.INACTIVE);
    };

    return (
        <ModalBody
            title={t('dump')}
            subtitle={isEditMode ? t('update') : t('add')}
            open={showEditModal}
            handleClose={() => setShowEditModal(false)}
            disableSubmit={
                !selectedDesignation.length ||
                !selectedCodeProC.length ||
                (selectedDisplayOrder !== null && selectedDisplayOrder < 0) ||
                (selectedVolume !== null && selectedVolume < 0) ||
                selectedVolume === null
            }
            onSubmit={() => {
                isEditMode
                    ? mutateDump({
                          dumpId: dump?._id || '',
                          data: {
                              designation: selectedDesignation,
                              crudStatus: selectedCrudStatus,
                              codeProC: selectedCodeProC,
                              volume: selectedVolume ?? 0,
                              displayOrder: selectedDisplayOrder,
                          },
                      })
                    : addDump({
                          designation: selectedDesignation,
                          codeProC: selectedCodeProC,
                          volume: selectedVolume ?? 0,
                          displayOrder: selectedDisplayOrder,
                      });
            }}
        >
            <CustomTextInput value={selectedDesignation} label={t('name')} onChange={setSelectedDesignation} required />
            <CustomTextInput value={selectedCodeProC} label={t('codeProC')} onChange={setSelectedCodeProC} required />
            <ToggleLine checked={selectedCrudStatus === crudStatusEnum.OK} onChange={handleActiveChange} title={capitalizeFirstLetter(t('active'))} />
            <CustomTextInput
                value={selectedVolume}
                label={t('volume')}
                onChange={(text) => setSelectedVolume(parseInt(text) || null)}
                type="number"
                required
            />
            <CustomTextInput
                value={selectedDisplayOrder}
                label={t('displayOrder')}
                onChange={(text) => setSelectedDisplayOrder(parseInt(text) || null)}
                type="number"
            />
        </ModalBody>
    );
};

export default UpdateDumpModal;
